import React, { useState, useEffect } from 'react';
import { FileText, Search, User, Shield, AlertTriangle, Users, MapPin, MessageSquare, Calendar, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface ActivityLog {
  id: number;
  admin_name: string | null;
  user_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  description: string;
  ip_address: string | null;
  created_at: string;
}

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');

  useEffect(() => { fetchLogs(); }, [page, filterAction, filterEntityType]);

  const fetchLogs = async () => {
    setRefreshing(true);
    try {
      const skip = (page - 1) * limit;
      const params: any = { skip, limit };
      if (filterAction) params.action = filterAction;
      if (filterEntityType) params.entity_type = filterEntityType;
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/activity-logs', { params });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getActionIcon = (action: string) => {
    if (action.includes('user')) return <User className="w-4 h-4" />;
    if (action.includes('family')) return <Users className="w-4 h-4" />;
    if (action.includes('emergency')) return <AlertTriangle className="w-4 h-4" />;
    if (action.includes('message')) return <MessageSquare className="w-4 h-4" />;
    if (action.includes('place')) return <MapPin className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'text-green-600 bg-green-100';
    if (action.includes('update')) return 'text-blue-600 bg-blue-100';
    if (action.includes('delete')) return 'text-red-600 bg-red-100';
    if (action.includes('broadcast')) return 'text-purple-600 bg-purple-100';
    return 'text-gray-600 bg-gray-100';
  };

  const parseAdminTimestamp = (value: string): Date | null => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      if (!Number.isFinite(n)) return null;
      const ms = raw.length <= 10 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    let normalized = raw.replace(" ", "T");

    normalized = normalized.replace(/\s+UTC$/, "Z");
    normalized = normalized.replace(/([+-]\d{2}:\d{2})Z$/, "$1");
    normalized = normalized.replace(/([+-]\d{2})(\d{2})Z$/, "$1:$2");
    normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");

    normalized = normalized.replace(
      /(\.\d{4,})(Z|[+-]\d{2}:\d{2})?$/,
      (_, fraction: string, tz: string | undefined) => `.${fraction.slice(1, 4)}${tz ?? ""}`
    );

    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
    if (!hasTimezone) normalized = `${normalized}Z`;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatAbsoluteTr = (d: Date) =>
    d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });

  const formatRelativeTr = (d: Date) => {
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (!Number.isFinite(diffMs)) return '-';
    if (diffMs < 0) return 'Az önce';

    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (days > 0) return `${days} gün önce`;
    if (hours > 0) return `${hours} saat önce`;
    if (minutes > 0) return `${minutes} dk önce`;
    return 'Az önce';
  };

  const renderTimeCell = (timestamp: string) => {
    const d = parseAdminTimestamp(timestamp);
    if (!d) return <span className="text-xs text-gray-400">-</span>;
    return (
      <div className="leading-tight" title={timestamp}>
        <div className="text-xs text-gray-700 whitespace-nowrap">{formatAbsoluteTr(d)}</div>
        <div className="text-[11px] text-gray-500 whitespace-nowrap">{formatRelativeTr(d)}</div>
      </div>
    );
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return log.admin_name?.toLowerCase().includes(search) || log.user_name?.toLowerCase().includes(search) || log.action.toLowerCase().includes(search) || log.description?.toLowerCase().includes(search);
  });

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Activity Logs</h1>
            <p className="text-sm text-gray-500 mt-1">Tüm sistem aktivitelerini görüntüle</p>
          </div>
          <button onClick={fetchLogs} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Admin, kullanıcı veya işlem ara..." className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={filterAction} onChange={(e) => { setFilterAction(e.target.value); setPage(1); }} className="px-4 py-2 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500">
              <option value="">Tüm İşlemler</option>
              <option value="user_created">Kullanıcı Oluşturma</option>
              <option value="user_updated">Kullanıcı Güncelleme</option>
              <option value="user_deleted">Kullanıcı Silme</option>
              <option value="broadcast_sent">Toplu Bildirim</option>
              <option value="emergency_resolved">Acil Durum Çözüm</option>
            </select>
            <select value={filterEntityType} onChange={(e) => { setFilterEntityType(e.target.value); setPage(1); }} className="px-4 py-2 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500">
              <option value="">Tüm Tipler</option>
              <option value="user">Kullanıcı</option>
              <option value="family">Çevre</option>
              <option value="message">Mesaj</option>
              <option value="emergency">Acil Durum</option>
              <option value="place">Yer</option>
            </select>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1"><Shield className="w-4 h-4" />Toplam {total} kayıt</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />Sayfa {page} / {totalPages}</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl overflow-hidden">
          {filteredLogs.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Henüz aktivite kaydı bulunmuyor</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">İşlem</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Admin</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Kullanıcı</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Açıklama</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Zaman</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className={`flex items-center gap-2 px-2 py-1 rounded-xl w-fit ${getActionColor(log.action)}`}>
                            {getActionIcon(log.action)}
                            <span className="text-xs font-medium">{log.action}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{log.admin_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{log.user_name || '-'}</td>
                        <td className="px-4 py-3"><span className="text-sm text-gray-600 line-clamp-2">{log.description || '-'}</span></td>
                        <td className="px-4 py-3">{renderTimeCell(log.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-100">
                {filteredLogs.map((log) => (
                  <div key={log.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-xl ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                        <span className="text-xs font-medium">{log.action}</span>
                      </div>
                      <div className="text-right">{renderTimeCell(log.created_at)}</div>
                    </div>
                    <p className="text-sm text-gray-800 mb-2 line-clamp-2">{log.description || '-'}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Shield className="w-3 h-3" />{log.admin_name || '-'}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{log.user_name || '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-4 py-2 bg-white rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Önceki</button>
            <span className="text-sm text-gray-600">{page} / {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-4 py-2 bg-white rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Sonraki</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
